import React, { useEffect, useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useAuth } from '../hooks/useAuth';
import './Help.css';

const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (character) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[character]));

export default function Help() {
  const { activeEmployeeId, activeUserEmail, activeUserRoles, loading: authLoading } = useAuth();
  const roleHeader = (activeUserRoles || []).join(',');
  const [markdown, setMarkdown] = useState(null);
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contactError, setContactError] = useState(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (authLoading) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setContactError(null);
    setEmail(null);
    const contact = fetch(`/api/help?employeeId=${encodeURIComponent(activeEmployeeId || '167')}`, {
      signal: controller.signal,
      headers: {
        'x-kcs-user-email': activeUserEmail || '',
        'x-kcs-user-roles': roleHeader,
        'x-kcs-provider-employee-id': activeEmployeeId || '167',
      },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('Provider contact is unavailable.');
        return response.json();
      }).then((data) => {
        if (!controller.signal.aborted) setEmail(data.providerSuperUserEmail || null);
      }).catch((failure) => {
        if (!controller.signal.aborted) setContactError(failure.message);
      });
    const content = fetch('/help.md', { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error('Unable to load Help.');
        return response.text();
      }).then((text) => {
        if (!controller.signal.aborted) setMarkdown(text);
      }).catch((failure) => {
        if (!controller.signal.aborted) setError(failure.message);
      });
    Promise.all([contact, content]).then(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [authLoading, activeEmployeeId, activeUserEmail, roleHeader, retry]);

  if (loading) return <div className="loading">Loading Help...</div>;
  if (error) return <div role="alert">{error} <button type="button" onClick={() => setRetry((current) => current + 1)}>Retry</button></div>;

  const parsed = marked.parse(markdown || '', { async: false });
  const html = DOMPurify.sanitize(parsed.replaceAll('{{providerSuperUserEmail}}', escapeHtml(email || 'Not configured')));
  return (
    <section className="help-page">
      {contactError && <p role="alert">{contactError} <button type="button" onClick={() => setRetry((current) => current + 1)}>Retry</button></p>}
      <article className="help-content" dangerouslySetInnerHTML={{ __html: html }} />
    </section>
  );
}